var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState, useCallback, useRef, useEffect } from "react";
import { isValidCardId } from "../../../models/CardRecommendation";
import { useEscalationTypeRecommenderAgent } from "./agents";
import { logEvent, logError } from "../../../library/logging/loggingService";
/**
 * Encapsulates the AI card recommendation business logic:
 * agent invocation, response parsing, validation, telemetry,
 * and stale-response management.
 *
 * The component consuming this hook only needs to wire the returned
 * callbacks to the RecommendationInput and read the result state.
 *
 * @param options.enabled - When false the hook is inert: no agent is
 *   initialised, no requests are made. This satisfies the React rules-of-hooks
 *   requirement (the hook is always called) while avoiding unnecessary work
 *   when the feature flag is off.
 */
export const useCardRecommendation = (options = {}) => {
    const { enabled = true } = options;
    const [recommendedCardId, setRecommendedCardId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [statusHint, setStatusHint] = useState(null);
    const agent = useEscalationTypeRecommenderAgent();
    const agentRef = useRef(agent);
    agentRef.current = agent;
    // Monotonically increasing counter to discard stale responses
    const activeRequestRef = useRef(0);
    // AbortController for cleanup on unmount — replaces isMountedRef anti-pattern.
    // Controller is created inside useEffect so React StrictMode double-mount
    // gets a fresh (non-aborted) controller on re-mount.
    const abortControllerRef = useRef(new AbortController());
    useEffect(() => {
        abortControllerRef.current = new AbortController();
        return () => { abortControllerRef.current.abort(); };
    }, []);
    const handleQueryReady = useCallback((query) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c;
        // Short-circuit when the feature is disabled — avoids agent initialisation
        // and network calls while still obeying React rules-of-hooks.
        if (!enabled)
            return;
        if (!query.trim()) {
            // Invalidate any in-flight request so stale responses are discarded
            ++activeRequestRef.current;
            setRecommendedCardId(null);
            setStatusHint(null);
            setIsLoading(false);
            return;
        }
        const requestId = ++activeRequestRef.current;
        setIsLoading(true);
        setStatusHint(null);
        try {
            const response = yield agentRef.current.sendJson(query, { signal: abortControllerRef.current.signal });
            // Discard stale responses or if component unmounted (aborted)
            if (requestId !== activeRequestRef.current || abortControllerRef.current.signal.aborted) {
                return;
            }
            const recommended = (_a = response === null || response === void 0 ? void 0 : response.recommendedTypes) === null || _a === void 0 ? void 0 : _a[0];
            if (recommended && isValidCardId(recommended)) {
                setRecommendedCardId(recommended);
                setStatusHint(null);
                logEvent("CardRecommendation.ResultDisplayed", {
                    component: "Escalations",
                    action: "result_displayed",
                    recommendedCardCode: recommended,
                    totalResults: (_c = (_b = response === null || response === void 0 ? void 0 : response.recommendedTypes) === null || _b === void 0 ? void 0 : _b.length) !== null && _c !== void 0 ? _c : 0,
                });
            }
            else {
                setRecommendedCardId(null);
                setStatusHint("no_result");
                logEvent("CardRecommendation.NoResult", {
                    component: "Escalations",
                    action: "no_result",
                    inputLength: query.length,
                });
            }
        }
        catch (e) {
            // Discard stale error responses or if component unmounted (aborted)
            if (requestId !== activeRequestRef.current || abortControllerRef.current.signal.aborted) {
                return;
            }
            const is429 = e instanceof Error && /429|too_many_requests/i.test(e.message);
            const errorContext = {
                component: "Escalations",
                action: "recommendation_error",
                errorType: is429 ? "throttled" : (e instanceof Error ? e.name : "Unknown"),
            };
            // logError → App Insights Exceptions pipeline (operational alerting).
            // logEvent → App Insights Custom Events table (feature dashboard consistency
            // with other CardRecommendation.* events). Both channels are intentional.
            logError("Card recommendation failed", errorContext);
            logEvent("CardRecommendation.Error", errorContext);
            setRecommendedCardId(null);
            setStatusHint(is429 ? "throttled" : "error");
        }
        finally {
            if (requestId === activeRequestRef.current && !abortControllerRef.current.signal.aborted) {
                setIsLoading(false);
            }
        }
    }), [enabled]);
    return {
        recommendedCardId,
        isLoading,
        statusHint,
        handleQueryReady,
    };
};
